import React, { useState, useEffect } from 'react';
import {
  Receipt,
  ChevronDown,
  Building2,
  Calendar,
  Eye,
  Trash2,
  Search,
  Filter,
  RefreshCw,
  IndianRupee,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Send,
  CreditCard,
  FileDown,
  Loader2,
  Paperclip,
  X,
  Edit2,
  Calculator,
} from 'lucide-react';
import { invoiceAPI, establishmentAPI } from '../api/apiService';
import { notifySavedAndReload } from '../utils/notify';
import { useAuth, useActiveEstId } from './AuthContext';
import './UpdateEmployee.css';

const ViewInvoices = () => {
  const { user } = useAuth();
  const lockedEstId = useActiveEstId();

  // State management
  const [invoices, setInvoices] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [summary, setSummary] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    establishment_id: lockedEstId || '',
    status: '',
    search: '',
  });

  // Payment form
  const [paymentData, setPaymentData] = useState({
    invoice_id: '',
    amount: '',
    payment_method: 'bank_transfer',
    reference_number: '',
    notes: '',
  });

  // PDF loading state
  const [pdfLoading, setPdfLoading] = useState(null);

  // Edit invoice (financials) modal — only opens for draft invoices.
  // Two modes: "components" (edit each line, auto-recalc) and "override" (set grand_total directly).
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editMode, setEditMode] = useState('components'); // 'components' | 'override'
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    gross_amount: '',
    service_charge_amount: '',
    discount_amount: '',
    cgst_amount: '',
    sgst_amount: '',
    igst_amount: '',
    grand_total: '',
    override_reason: '',
  });

  const openEditModal = (invoice) => {
    setEditInvoice(invoice);
    setEditMode('components');
    setEditForm({
      gross_amount: invoice.gross_amount ?? '',
      service_charge_amount: invoice.service_charge_amount ?? '',
      discount_amount: invoice.discount_amount ?? 0,
      cgst_amount: invoice.cgst_amount ?? 0,
      sgst_amount: invoice.sgst_amount ?? 0,
      igst_amount: invoice.igst_amount ?? 0,
      grand_total: invoice.grand_total ?? '',
      override_reason: '',
    });
    setShowEditModal(true);
  };

  // Live re-compute for the components view
  const editSubtotal = (
    parseFloat(editForm.gross_amount || 0)
    + parseFloat(editForm.service_charge_amount || 0)
    - parseFloat(editForm.discount_amount || 0)
  );
  const editTax = (
    parseFloat(editForm.cgst_amount || 0)
    + parseFloat(editForm.sgst_amount || 0)
    + parseFloat(editForm.igst_amount || 0)
  );
  const editComputedTotal = editSubtotal + editTax;

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editInvoice) return;
    setEditSaving(true);
    try {
      let payload;
      if (editMode === 'override') {
        if (!editForm.grand_total) {
          alert('Please enter a grand total amount.');
          setEditSaving(false);
          return;
        }
        payload = {
          manual_override: true,
          grand_total: parseFloat(editForm.grand_total),
          // Send current tax components so the backend can derive subtotal correctly
          cgst_amount: parseFloat(editForm.cgst_amount || 0),
          sgst_amount: parseFloat(editForm.sgst_amount || 0),
          igst_amount: parseFloat(editForm.igst_amount || 0),
          override_reason: editForm.override_reason || null,
        };
      } else {
        payload = {
          gross_amount: parseFloat(editForm.gross_amount || 0),
          service_charge_amount: parseFloat(editForm.service_charge_amount || 0),
          discount_amount: parseFloat(editForm.discount_amount || 0),
          cgst_amount: parseFloat(editForm.cgst_amount || 0),
          sgst_amount: parseFloat(editForm.sgst_amount || 0),
          igst_amount: parseFloat(editForm.igst_amount || 0),
        };
      }
      await invoiceAPI.updateStatus(editInvoice.id, payload);
      notifySavedAndReload('Invoice updated.');
    } catch (err) {
      alert('Failed to update invoice: ' + (err.message || 'Unknown error'));
      setEditSaving(false);
    }
  };

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    body: '',
    attachments: [],
    establishmentId: null,
  });
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  // S3 File Browser state
  const [showS3Browser, setShowS3Browser] = useState(false);
  const [s3Files, setS3Files] = useState([]);
  const [loadingS3Files, setLoadingS3Files] = useState(false);
  const [selectedS3Files, setSelectedS3Files] = useState([]);

  const statusConfig = {
    draft: { label: 'Draft', color: '#6b7280', bg: '#f8f9fa', icon: Clock },
    pending: { label: 'Pending', color: '#d97706', bg: '#fef3c7', icon: Clock },
    sent: { label: 'Sent', color: '#2563eb', bg: '#dbeafe', icon: Send },
    paid: { label: 'Paid', color: '#059669', bg: '#d1fae5', icon: CheckCircle },
    partially_paid: { label: 'Partial', color: '#7c3aed', bg: '#ede9fe', icon: CreditCard },
    overdue: { label: 'Overdue', color: '#dc2626', bg: '#fee2e2', icon: AlertTriangle },
    cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#e5e7eb', icon: XCircle },
  };

  // Sync when MultiClientAdmin switches org
  useEffect(() => {
    if (lockedEstId) setFilters(prev => ({ ...prev, establishment_id: lockedEstId }));
  }, [lockedEstId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch data on mount
  useEffect(() => {
    fetchEstablishments();
    fetchInvoices();
    fetchSummary();
  }, []);

  const fetchEstablishments = async () => {
    if (lockedEstId) return; // ClientAdmin: already set via locked est
    try {
      const response = await establishmentAPI.getAll();
      setEstablishments(response.establishments || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.establishment_id) params.establishment_id = filters.establishment_id;
      if (filters.status) params.status = filters.status;

      const response = await invoiceAPI.getList(params);
      let invoiceList = response.invoices || [];

      // Apply search filter client-side
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        invoiceList = invoiceList.filter(
          (inv) =>
            inv.invoice_number?.toLowerCase().includes(searchLower) ||
            inv.client_name?.toLowerCase().includes(searchLower)
        );
      }

      setInvoices(invoiceList);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError('Error loading invoices: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await invoiceAPI.getSummary(
        filters.establishment_id || null
      );
      setSummary(response);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  // Refetch when filters change
  useEffect(() => {
    fetchInvoices();
    fetchSummary();
  }, [filters.establishment_id, filters.status]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = (e) => {
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchInvoices();
  };

  const handleStatusUpdate = async (invoiceId, newStatus) => {
    try {
      await invoiceAPI.updateStatus(invoiceId, { status: newStatus });
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Error updating status: ' + error.message);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) {
      return;
    }

    try {
      await invoiceAPI.delete(invoiceId);
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      setError('Error deleting invoice: ' + error.message);
    }
  };

  const openPaymentModal = (invoice) => {
    setPaymentData({
      invoice_id: invoice.id,
      amount: (invoice.grand_total - (invoice.paid_amount || 0)).toFixed(2),
      payment_method: 'bank_transfer',
      reference_number: '',
      notes: '',
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await invoiceAPI.recordPayment({
        ...paymentData,
        amount: parseFloat(paymentData.amount),
        payment_date: new Date().toISOString().split('T')[0],
      });
      setShowPaymentModal(false);
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      console.error('Error recording payment:', error);
      setError('Error recording payment: ' + error.message);
    }
  };

  const handleDownloadPDF = async (invoice, openInNewTab = false) => {
    try {
      setPdfLoading(invoice.id);

      // Get attachment keys from invoice
      const attachmentKeys = invoice.attachments?.map(att => att.s3_key) || [];

      // Download all files (PDF + attachments) as ZIP
      const blob = await invoiceAPI.downloadAllFiles(invoice.id, attachmentKeys);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoice.invoice_number.replace(/\//g, '_')}_All_Files.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL after use
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Error downloading invoice files:', error);
      setError('Error downloading invoice files: ' + error.message);
    } finally {
      setPdfLoading(null);
    }
  };

  const openEmailModal = async (invoice) => {
    // Prepare email with invoice details
    const emailBody = `Dear ${invoice.client_name},

Please find attached the invoice for the billing period ${formatDate(invoice.billing_period_start)} to ${formatDate(invoice.billing_period_end)}.

Invoice Details:
- Invoice Number: ${invoice.invoice_number}
- Amount: ${formatCurrency(invoice.grand_total)}
- Due Date: ${formatDate(invoice.due_date)}

Please remit payment at your earliest convenience.

Best regards,
${invoice.establishment_name}`;

    // Prepare initial attachments list (invoice PDF)
    const attachments = [];

    // Add invoice PDF if available
    if (invoice.pdf_s3_url && invoice.pdf_s3_key) {
      attachments.push({
        name: `Invoice_${invoice.invoice_number.replace(/\//g, '_')}.pdf`,
        url: invoice.pdf_s3_url,
        s3_key: invoice.pdf_s3_key,
        type: 'invoice_pdf',
        size: 0,
      });
    }

    setEmailData({
      to: invoice.client_email || '',
      subject: `Invoice ${invoice.invoice_number} - ${invoice.client_name}`,
      body: emailBody,
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoice_number,
      establishmentId: invoice.establishment_id,
      invoice: invoice, // Store the full invoice object
      attachments: attachments,
    });
    setShowEmailModal(true);
  };

  const handleBrowseS3Files = async () => {
    if (!emailData.invoice) {
      setError('Invoice data not found');
      return;
    }

    setLoadingS3Files(true);
    setShowS3Browser(true);

    try {
      const files = [];
      const invoice = emailData.invoice;

      // 1. Add invoice PDF if available
      if (invoice.pdf_s3_key && invoice.pdf_s3_url) {
        files.push({
          name: `Invoice_${invoice.invoice_number.replace(/\//g, '_')}.pdf`,
          s3_key: invoice.pdf_s3_key,
          s3_url: invoice.pdf_s3_url,
          presigned_url: invoice.pdf_s3_url,
          size: 0, // PDF size not tracked
          last_modified: invoice.created_at,
        });
      }

      // 2. Add all invoice attachments
      const invoiceAttachments = invoice.attachments || [];
      invoiceAttachments.forEach(att => {
        files.push({
          name: att.file_name || att.original_file_name,
          s3_key: att.s3_key,
          s3_url: att.s3_url,
          presigned_url: att.s3_url,
          size: att.file_size || 0,
          last_modified: att.uploaded_at,
        });
      });

      setS3Files(files);
    } catch (error) {
      console.error('Error loading invoice files:', error);
      setError('Error loading invoice files: ' + error.message);
    } finally {
      setLoadingS3Files(false);
    }
  };

  const handleToggleS3File = (file) => {
    setSelectedS3Files(prev => {
      const isSelected = prev.find(f => f.s3_key === file.s3_key);
      if (isSelected) {
        return prev.filter(f => f.s3_key !== file.s3_key);
      } else {
        return [...prev, file];
      }
    });
  };

  const handleAddS3Files = () => {
    const newAttachments = selectedS3Files.map(file => ({
      name: file.name,
      s3_key: file.s3_key,
      url: file.s3_url,
      presigned_url: file.presigned_url,
      type: 's3',
      size: file.size,
    }));

    setEmailData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...newAttachments],
    }));

    setSelectedS3Files([]);
    setShowS3Browser(false);
  };

  const handleAddAttachment = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setLoadingAttachments(true);

    try {
      const newAttachments = files.map(file => ({
        name: file.name,
        file: file,
        type: 'local',
        size: file.size,
      }));

      setEmailData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newAttachments],
      }));
    } catch (error) {
      console.error('Error adding attachments:', error);
      setError('Error adding attachments: ' + error.message);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleRemoveAttachment = (index) => {
    setEmailData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleSendEmail = async () => {
    // Here you would integrate with your email service
    // For now, we'll just mark the invoice as sent and close modal
    try {
      await handleStatusUpdate(emailData.invoiceId, 'sent');
      setShowEmailModal(false);
      alert(`Email would be sent to: ${emailData.to}\n\nSubject: ${emailData.subject}`);
    } catch (error) {
      console.error('Error sending email:', error);
      setError('Error sending email: ' + error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Anchor bare YYYY-MM-DD to local time — new Date('YYYY-MM-DD') parses
    // as UTC midnight which shifts to prior day in TZs west of UTC.
    const s = /^\d{4}-\d{2}-\d{2}$/.test(dateString) ? `${dateString}T00:00:00` : dateString;
    return new Date(s).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status) => {
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.35rem',
          padding: '0.25rem 0.75rem',
          borderRadius: '9999px',
          fontSize: '0.75rem',
          fontWeight: '500',
          color: config.color,
          background: config.bg,
        }}
      >
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  return (
    <div className="new-employee-container">
      <div className="new-employee-header-section">
        <div>
          <h2 className="new-employee-header">
            <Receipt size={28} style={{ marginRight: '12px' }} />
            View Invoices
          </h2>
          <p style={{ color: '#6b7280', fontSize: '0.95rem', marginTop: '8px' }}>
            View and manage all generated invoices
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
          }}
        >
          <div
            style={{
              background: 'var(--theme-surface, #ffffff)',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              padding: '1.25rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div
                style={{
                  background: '#dbeafe',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                }}
              >
                <Receipt size={20} style={{ color: '#2563eb' }} />
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>
                  Total Invoices
                </p>
                <p
                  style={{
                    color: '#111827',
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    margin: 0,
                  }}
                >
                  {summary.total_invoices || 0}
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              background: 'var(--theme-surface, #ffffff)',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              padding: '1.25rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div
                style={{
                  background: '#fef3c7',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                }}
              >
                <Clock size={20} style={{ color: '#d97706' }} />
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>
                  Pending Amount
                </p>
                <p
                  style={{
                    color: '#d97706',
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    margin: 0,
                  }}
                >
                  {formatCurrency(summary.pending_amount)}
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              background: 'var(--theme-surface, #ffffff)',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              padding: '1.25rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div
                style={{
                  background: '#d1fae5',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                }}
              >
                <CheckCircle size={20} style={{ color: '#059669' }} />
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>
                  Paid Amount
                </p>
                <p
                  style={{
                    color: '#059669',
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    margin: 0,
                  }}
                >
                  {formatCurrency(summary.paid_amount)}
                </p>
              </div>
            </div>
          </div>

          <div
            style={{
              background: 'var(--theme-surface, #ffffff)',
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              padding: '1.25rem',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div
                style={{
                  background: '#fee2e2',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                }}
              >
                <AlertTriangle size={20} style={{ color: '#dc2626' }} />
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>
                  Overdue
                </p>
                <p
                  style={{
                    color: '#dc2626',
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    margin: 0,
                  }}
                >
                  {summary.overdue_count || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="new-employee-content" style={{ marginBottom: '1.5rem' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
            alignItems: 'flex-end',
          }}
        >
          {/* Establishment Filter — hidden for ClientAdmin */}
          {!lockedEstId && (
          <div style={{ minWidth: '200px', flex: 1 }}>
            <label className="new-employee-field-label">
              <Building2 size={16} style={{ marginRight: '6px' }} />
              Establishment
            </label>
            <div style={{ position: 'relative' }}>
              <select
                name="establishment_id"
                value={filters.establishment_id}
                onChange={handleFilterChange}
                className="new-employee-input"
                style={{ appearance: 'none', paddingRight: '2.5rem' }}
              >
                <option value="">All Organizations</option>
                {establishments.map((est) => (
                  <option key={est.id} value={est.id}>
                    {est.name}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={20}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6b7280',
                }}
              />
            </div>
          </div>
          )}

          {/* Status Filter */}
          <div style={{ minWidth: '150px', flex: 1 }}>
            <label className="new-employee-field-label">
              <Filter size={16} style={{ marginRight: '6px' }} />
              Status
            </label>
            <div style={{ position: 'relative' }}>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="new-employee-input"
                style={{ appearance: 'none', paddingRight: '2.5rem' }}
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="partially_paid">Partially Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <ChevronDown
                size={20}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#6b7280',
                }}
              />
            </div>
          </div>

          {/* Search */}
          <div style={{ minWidth: '250px', flex: 2 }}>
            <label className="new-employee-field-label">
              <Search size={16} style={{ marginRight: '6px' }} />
              Search
            </label>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="text"
                value={filters.search}
                onChange={handleSearch}
                className="new-employee-input"
                placeholder="Invoice number or client name"
                style={{ flex: 1 }}
              />
              <button
                type="submit"
                className="new-employee-btn-next"
                style={{ minWidth: 'auto', padding: '0.5rem 1rem' }}
              >
                <Search size={16} />
              </button>
            </form>
          </div>

          {/* Refresh */}
          <button
            onClick={() => {
              fetchInvoices();
              fetchSummary();
            }}
            className="new-employee-btn-next"
            style={{ minWidth: 'auto', padding: '0.75rem' }}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div
          style={{
            padding: '1rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '0.5rem',
            color: '#dc2626',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {/* Invoice Table */}
      <div className="new-employee-content">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="new-employee-spinner" style={{ margin: '0 auto' }}></div>
            <p style={{ color: '#6b7280', marginTop: '1rem' }}>Loading invoices...</p>
          </div>
        ) : invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Receipt size={48} style={{ color: '#9ca3af', marginBottom: '1rem' }} />
            <p style={{ color: '#6b7280' }}>No invoices found</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '0.75rem',
                      color: '#374151',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                    }}
                  >
                    Invoice #
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '0.75rem',
                      color: '#374151',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                    }}
                  >
                    Client
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '0.75rem',
                      color: '#374151',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                    }}
                  >
                    Period
                  </th>
                  <th
                    style={{
                      textAlign: 'right',
                      padding: '0.75rem',
                      color: '#374151',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                    }}
                  >
                    Amount
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '0.75rem',
                      color: '#374151',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                    }}
                  >
                    Status
                  </th>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '0.75rem',
                      color: '#374151',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                    }}
                  >
                    Due Date
                  </th>
                  <th
                    style={{
                      textAlign: 'center',
                      padding: '0.75rem',
                      color: '#374151',
                      fontWeight: '600',
                      fontSize: '0.875rem',
                    }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    style={{
                      borderBottom: '1px solid #e5e7eb',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#ffffff')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={{ padding: '0.75rem' }}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setSelectedInvoice(invoice);
                        }}
                        style={{
                          fontWeight: '600',
                          color: '#2563eb',
                          textDecoration: 'none',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                      >
                        {invoice.invoice_number}
                      </a>
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div>
                        <p style={{ margin: 0, fontWeight: '500' }}>{invoice.client_name}</p>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>
                          {invoice.establishment_name}
                        </p>
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                      {formatDate(invoice.billing_period_start)} -{' '}
                      {formatDate(invoice.billing_period_end)}
                    </td>
                    <td
                      style={{
                        padding: '0.75rem',
                        textAlign: 'right',
                        fontWeight: '600',
                      }}
                    >
                      {formatCurrency(invoice.grand_total)}
                      {invoice.paid_amount > 0 && invoice.paid_amount < invoice.grand_total && (
                        <p
                          style={{
                            margin: 0,
                            fontSize: '0.75rem',
                            color: '#059669',
                          }}
                        >
                          Paid: {formatCurrency(invoice.paid_amount)}
                        </p>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      {getStatusBadge(invoice.status)}
                    </td>
                    <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                      {formatDate(invoice.due_date)}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        {invoice.status === 'draft' && (
                          <button
                            onClick={() => openEditModal(invoice)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.1)';
                              e.currentTarget.style.background = '#e0e7ff';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.background = '#eef2ff';
                            }}
                            style={{
                              background: '#eef2ff',
                              border: 'none',
                              borderRadius: '0.375rem',
                              padding: '0.5rem',
                              cursor: 'pointer',
                              color: '#4f46e5',
                              transition: 'all 0.1s ease',
                            }}
                            title="Edit invoice amount"
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                        {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                          <button
                            onClick={() => openPaymentModal(invoice)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.1)';
                              e.currentTarget.style.background = '#a7f3d0';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.background = '#d1fae5';
                            }}
                            style={{
                              background: '#d1fae5',
                              border: 'none',
                              borderRadius: '0.375rem',
                              padding: '0.5rem',
                              cursor: 'pointer',
                              color: '#059669',
                              transition: 'all 0.1s ease',
                            }}
                            title="Record Payment"
                          >
                            <CreditCard size={16} />
                          </button>
                        )}
                        {invoice.status !== 'cancelled' && (
                          <button
                            onClick={() => openEmailModal(invoice)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.1)';
                              e.currentTarget.style.background = '#bfdbfe';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.background = '#dbeafe';
                            }}
                            style={{
                              background: '#dbeafe',
                              border: 'none',
                              borderRadius: '0.375rem',
                              padding: '0.5rem',
                              cursor: 'pointer',
                              color: '#2563eb',
                              transition: 'all 0.1s ease',
                            }}
                            title="Send Email"
                          >
                            <Send size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDownloadPDF(invoice)}
                          disabled={pdfLoading === invoice.id}
                          onMouseEnter={(e) => {
                            if (pdfLoading !== invoice.id) {
                              e.currentTarget.style.transform = 'scale(1.1)';
                              e.currentTarget.style.background = '#fde68a';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.background = '#fef3c7';
                          }}
                          style={{
                            background: '#fef3c7',
                            border: 'none',
                            borderRadius: '0.375rem',
                            padding: '0.5rem',
                            cursor: pdfLoading === invoice.id ? 'wait' : 'pointer',
                            color: '#d97706',
                            opacity: pdfLoading === invoice.id ? 0.7 : 1,
                            transition: 'all 0.1s ease',
                          }}
                          title="Download PDF"
                        >
                          {pdfLoading === invoice.id ? (
                            <Loader2 size={16} className="animate-spin" style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <FileDown size={16} />
                          )}
                        </button>
                        {invoice.status === 'draft' && (
                          <button
                            onClick={() => handleDeleteInvoice(invoice.id)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.1)';
                              e.currentTarget.style.background = '#fecaca';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              e.currentTarget.style.background = '#fee2e2';
                            }}
                            style={{
                              background: '#fee2e2',
                              border: 'none',
                              borderRadius: '0.375rem',
                              padding: '0.5rem',
                              cursor: 'pointer',
                              color: '#dc2626',
                              transition: 'all 0.1s ease',
                            }}
                            title="Delete Invoice"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => setSelectedInvoice(null)}
        >
          <div
            style={{
              background: 'var(--theme-surface, #ffffff)',
              borderRadius: '0.75rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '1.5rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <h3 style={{ margin: 0, fontWeight: '600' }}>
                Invoice #{selectedInvoice.invoice_number}
              </h3>
              {getStatusBadge(selectedInvoice.status)}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '1rem',
              }}
            >
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>
                  Client
                </p>
                <p style={{ fontWeight: '500', margin: '0.25rem 0' }}>
                  {selectedInvoice.client_name}
                </p>
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>
                  GSTIN
                </p>
                <p style={{ fontWeight: '500', margin: '0.25rem 0' }}>
                  {selectedInvoice.client_gstin || '-'}
                </p>
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>
                  Billing Period
                </p>
                <p style={{ fontWeight: '500', margin: '0.25rem 0' }}>
                  {formatDate(selectedInvoice.billing_period_start)} -{' '}
                  {formatDate(selectedInvoice.billing_period_end)}
                </p>
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.75rem', margin: 0 }}>
                  Due Date
                </p>
                <p style={{ fontWeight: '500', margin: '0.25rem 0' }}>
                  {formatDate(selectedInvoice.due_date)}
                </p>
              </div>
            </div>

            <hr style={{ margin: '1.5rem 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

            <div style={{ display: 'grid', gap: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Total Employees</span>
                <span style={{ fontWeight: '500' }}>{selectedInvoice.total_employees}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Gross Amount</span>
                <span style={{ fontWeight: '500' }}>
                  {formatCurrency(selectedInvoice.gross_amount)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>
                  Service Charges ({selectedInvoice.service_charge_rate}%)
                </span>
                <span style={{ fontWeight: '500' }}>
                  {formatCurrency(selectedInvoice.service_charge_amount)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Subtotal</span>
                <span style={{ fontWeight: '500' }}>
                  {formatCurrency(selectedInvoice.subtotal)}
                </span>
              </div>

              {selectedInvoice.is_igst ? (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280' }}>
                    IGST ({selectedInvoice.igst_rate}%)
                  </span>
                  <span style={{ fontWeight: '500' }}>
                    {formatCurrency(selectedInvoice.igst_amount)}
                  </span>
                </div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>
                      CGST ({selectedInvoice.cgst_rate}%)
                    </span>
                    <span style={{ fontWeight: '500' }}>
                      {formatCurrency(selectedInvoice.cgst_amount)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>
                      SGST ({selectedInvoice.sgst_rate}%)
                    </span>
                    <span style={{ fontWeight: '500' }}>
                      {formatCurrency(selectedInvoice.sgst_amount)}
                    </span>
                  </div>
                </>
              )}

              <hr style={{ margin: '0.5rem 0', border: 'none', borderTop: '1px solid #e5e7eb' }} />

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '1.125rem',
                }}
              >
                <span style={{ fontWeight: '600' }}>Grand Total</span>
                <span style={{ fontWeight: '700', color: '#059669' }}>
                  {formatCurrency(selectedInvoice.grand_total)}
                </span>
              </div>

              {selectedInvoice.paid_amount > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#059669' }}>Paid Amount</span>
                    <span style={{ fontWeight: '500', color: '#059669' }}>
                      {formatCurrency(selectedInvoice.paid_amount)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#dc2626' }}>Balance Due</span>
                    <span style={{ fontWeight: '500', color: '#dc2626' }}>
                      {formatCurrency(
                        selectedInvoice.grand_total - selectedInvoice.paid_amount
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>

            {selectedInvoice.amount_in_words && (
              <p
                style={{
                  marginTop: '1rem',
                  color: '#6b7280',
                  fontSize: '0.875rem',
                  fontStyle: 'italic',
                }}
              >
                {selectedInvoice.amount_in_words}
              </p>
            )}

            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="new-employee-btn-next"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal — financials, draft-only */}
      {showEditModal && editInvoice && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '1rem' }}
          onClick={() => !editSaving && setShowEditModal(false)}
        >
          <div
            style={{ background: 'var(--theme-surface, #ffffff)', borderRadius: '0.75rem',
              maxWidth: '560px', width: '100%', padding: '1.5rem',
              maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
              <div>
                <h3 style={{ margin: 0, fontWeight: 600 }}>
                  <Edit2 size={18} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                  Edit Invoice {editInvoice.invoice_number}
                </h3>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                  {editInvoice.client_name}
                </p>
              </div>
              <button onClick={() => setShowEditModal(false)} disabled={editSaving}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                <X size={20} />
              </button>
            </div>

            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: '0.5rem', margin: '1rem 0', padding: '0.25rem',
              background: '#f3f4f6', borderRadius: '0.5rem' }}>
              <button type="button" onClick={() => setEditMode('components')}
                style={{ flex: 1, padding: '0.5rem', border: 'none', borderRadius: '0.375rem',
                  background: editMode === 'components' ? '#4F46E5' : 'transparent',
                  color: editMode === 'components' ? '#fff' : '#374151',
                  cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}>
                <Calculator size={14} /> Edit components
              </button>
              <button type="button" onClick={() => setEditMode('override')}
                style={{ flex: 1, padding: '0.5rem', border: 'none', borderRadius: '0.375rem',
                  background: editMode === 'override' ? '#4F46E5' : 'transparent',
                  color: editMode === 'override' ? '#fff' : '#374151',
                  cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}>
                Override total
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              {editMode === 'components' ? (
                <>
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 0 }}>
                    Edit any line; subtotal and grand total recalculate automatically.
                  </p>
                  {[
                    { key: 'gross_amount', label: 'Gross (wages)' },
                    { key: 'service_charge_amount', label: 'Service charge' },
                    { key: 'discount_amount', label: 'Discount' },
                    { key: 'cgst_amount', label: 'CGST' },
                    { key: 'sgst_amount', label: 'SGST' },
                    { key: 'igst_amount', label: 'IGST' },
                  ].map(({ key, label }) => (
                    <div key={key} style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <label style={{ flex: 1, fontSize: '0.85rem', color: '#374151' }}>{label}</label>
                      <input type="number" step="0.01" min="0"
                        value={editForm[key]}
                        onChange={e => setEditForm(p => ({ ...p, [key]: e.target.value }))}
                        className="new-employee-input" style={{ maxWidth: '200px', textAlign: 'right' }} />
                    </div>
                  ))}
                  <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f9fafb',
                    borderRadius: '0.5rem', fontSize: '0.85rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Subtotal</span>
                      <strong>₹{editSubtotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Tax</span>
                      <strong>₹{editTax.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem',
                      paddingTop: '0.5rem', borderTop: '1px solid #e5e7eb', fontSize: '1rem' }}>
                      <span style={{ fontWeight: 600 }}>Grand total</span>
                      <strong style={{ color: '#4f46e5' }}>
                        ₹{editComputedTotal.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </strong>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 0 }}>
                    Override the grand total directly. The reason is stored on the invoice notes.
                  </p>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label className="new-employee-field-label">
                      <IndianRupee size={14} style={{ marginRight: '4px' }} /> Grand total <span className="new-employee-required">*</span>
                    </label>
                    <input type="number" step="0.01" min="0" required
                      value={editForm.grand_total}
                      onChange={e => setEditForm(p => ({ ...p, grand_total: e.target.value }))}
                      className="new-employee-input" />
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                      Was ₹{Number(editInvoice.grand_total || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label className="new-employee-field-label">Reason for override</label>
                    <textarea rows={2} value={editForm.override_reason}
                      onChange={e => setEditForm(p => ({ ...p, override_reason: e.target.value }))}
                      className="new-employee-input"
                      placeholder="e.g., Negotiated discount, salary correction…" />
                  </div>
                </>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button type="button" onClick={() => setShowEditModal(false)} disabled={editSaving}
                  style={{ padding: '0.5rem 1rem', border: '1px solid #d1d5db',
                    borderRadius: '0.375rem', background: '#fff', color: '#374151', cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" disabled={editSaving}
                  style={{ padding: '0.5rem 1.25rem', border: 'none', borderRadius: '0.375rem',
                    background: '#4f46e5', color: '#fff', cursor: 'pointer', fontWeight: 500,
                    display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                  {editSaving ? <><Loader2 size={14} className="vi-spin" /> Saving…</> : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            style={{
              background: 'var(--theme-surface, #ffffff)',
              borderRadius: '0.75rem',
              maxWidth: '400px',
              width: '100%',
              padding: '1.5rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1.5rem 0', fontWeight: '600' }}>
              <CreditCard size={20} style={{ marginRight: '0.5rem' }} />
              Record Payment
            </h3>

            <form onSubmit={handlePaymentSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label className="new-employee-field-label">
                  <IndianRupee size={16} style={{ marginRight: '6px' }} />
                  Amount <span className="new-employee-required">*</span>
                </label>
                <input
                  type="number"
                  value={paymentData.amount}
                  onChange={(e) =>
                    setPaymentData((prev) => ({ ...prev, amount: e.target.value }))
                  }
                  className="new-employee-input"
                  step="0.01"
                  min="0"
                  required
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label className="new-employee-field-label">Payment Method</label>
                <div style={{ position: 'relative' }}>
                  <select
                    value={paymentData.payment_method}
                    onChange={(e) =>
                      setPaymentData((prev) => ({
                        ...prev,
                        payment_method: e.target.value,
                      }))
                    }
                    className="new-employee-input"
                    style={{ appearance: 'none', paddingRight: '2.5rem' }}
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="upi">UPI</option>
                    <option value="cash">Cash</option>
                    <option value="other">Other</option>
                  </select>
                  <ChevronDown
                    size={20}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      pointerEvents: 'none',
                      color: '#6b7280',
                    }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label className="new-employee-field-label">Reference Number</label>
                <input
                  type="text"
                  value={paymentData.reference_number}
                  onChange={(e) =>
                    setPaymentData((prev) => ({
                      ...prev,
                      reference_number: e.target.value,
                    }))
                  }
                  className="new-employee-input"
                  placeholder="Transaction ID / Cheque No."
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label className="new-employee-field-label">Notes</label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) =>
                    setPaymentData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  className="new-employee-input"
                  rows="2"
                  style={{ resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  style={{
                    padding: '0.5rem 1rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    background: 'var(--theme-surface, #ffffff)',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="new-employee-btn-create">
                  <CheckCircle size={16} />
                  Record Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => setShowEmailModal(false)}
        >
          <div
            style={{
              background: 'var(--theme-surface, #ffffff)',
              borderRadius: '0.75rem',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: '1.5rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <h3 style={{ margin: 0, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Send size={20} />
                Send Invoice via Email
              </h3>
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Invoice #{emailData.invoiceNumber}
              </span>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="new-employee-field-label">
                To <span className="new-employee-required">*</span>
              </label>
              <input
                type="email"
                value={emailData.to}
                onChange={(e) => setEmailData((prev) => ({ ...prev, to: e.target.value }))}
                className="new-employee-input"
                placeholder="recipient@example.com"
                required
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label className="new-employee-field-label">
                Subject <span className="new-employee-required">*</span>
              </label>
              <input
                type="text"
                value={emailData.subject}
                onChange={(e) => setEmailData((prev) => ({ ...prev, subject: e.target.value }))}
                className="new-employee-input"
                placeholder="Email subject"
                required
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label className="new-employee-field-label">
                Message <span className="new-employee-required">*</span>
              </label>
              <textarea
                value={emailData.body}
                onChange={(e) => setEmailData((prev) => ({ ...prev, body: e.target.value }))}
                className="new-employee-input"
                rows="12"
                style={{
                  resize: 'vertical',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                }}
                placeholder="Email message body"
                required
              />
            </div>

            <div
              style={{
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <FileDown size={16} style={{ color: '#0369a1' }} />
                  <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#0369a1' }}>
                    Attachments ({emailData.attachments.length})
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    type="button"
                    onClick={handleBrowseS3Files}
                    style={{
                      padding: '0.375rem 0.75rem',
                      background: 'var(--theme-surface, #ffffff)',
                      color: '#0369a1',
                      border: '1px solid #0369a1',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                    }}
                  >
                    <Search size={14} />
                    Cloud Files
                  </button>
                  <input
                    type="file"
                    id="email-attachment-input"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleAddAttachment}
                  />
                  <button
                    type="button"
                    onClick={() => document.getElementById('email-attachment-input').click()}
                    disabled={loadingAttachments}
                    style={{
                      padding: '0.375rem 0.75rem',
                      background: '#0369a1',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: loadingAttachments ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                    }}
                  >
                    <Paperclip size={14} />
                    {loadingAttachments ? 'Adding...' : 'Upload Files'}
                  </button>
                </div>
              </div>

              {emailData.attachments.length === 0 ? (
                <div style={{ fontSize: '0.875rem', color: '#6b7280', textAlign: 'center', padding: '1rem' }}>
                  No attachments added. Click "Add Files" to attach documents.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {emailData.attachments.map((attachment, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem',
                        background: 'var(--theme-surface, #ffffff)',
                        borderRadius: '0.375rem',
                        border: '1px solid #e0f2fe',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                        <FileDown size={14} style={{ color: '#0369a1' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--theme-text)' }}>
                            {attachment.name}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {attachment.type === 'invoice_pdf' && 'Invoice PDF'}
                            {attachment.type === 's3' && 'From S3'}
                            {attachment.type === 'local' && 'Local File'}
                            {!['invoice_pdf', 's3', 'local'].includes(attachment.type) && 'Attachment'}
                            {attachment.size > 0 && ` • ${(attachment.size / 1024).toFixed(2)} KB`}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(index)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#dc2626',
                          padding: '0.25rem',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="Remove attachment"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowEmailModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'var(--theme-surface, #ffffff)',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSendEmail}
                disabled={!emailData.to || !emailData.subject || !emailData.body}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  background: emailData.to && emailData.subject && emailData.body ? '#2563eb' : '#d1d5db',
                  color: '#fff',
                  cursor: emailData.to && emailData.subject && emailData.body ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: '500',
                }}
              >
                <Send size={16} />
                Send Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* S3 File Browser Modal */}
      {showS3Browser && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: '1rem',
          }}
          onClick={() => setShowS3Browser(false)}
        >
          <div
            style={{
              background: 'var(--theme-surface, #ffffff)',
              borderRadius: '0.75rem',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: '1.5rem',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
              }}
            >
              <h3 style={{ margin: 0, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Search size={20} />
                Browse Cloud Files
              </h3>
              <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                {selectedS3Files.length} selected
              </span>
            </div>

            {loadingS3Files ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#2563eb' }} />
                <p style={{ color: '#6b7280', marginTop: '1rem' }}>Loading files...</p>
              </div>
            ) : s3Files.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <FileDown size={48} style={{ color: '#9ca3af', marginBottom: '1rem' }} />
                <p style={{ color: '#6b7280' }}>No files found in S3</p>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  marginBottom: '1.5rem',
                }}
              >
                {s3Files.map((file, index) => {
                  const isSelected = selectedS3Files.find(f => f.s3_key === file.s3_key);
                  return (
                    <div
                      key={index}
                      onClick={() => handleToggleS3File(file)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem',
                        background: isSelected ? '#dbeafe' : '#ffffff',
                        border: `2px solid ${isSelected ? '#2563eb' : '#e5e7eb'}`,
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) e.currentTarget.style.background = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) e.currentTarget.style.background = '#ffffff';
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!!isSelected}
                        onChange={() => {}}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                        }}
                      />
                      <FileDown size={20} style={{ color: '#0369a1' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--theme-text)' }}>
                          {file.name}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {(file.size / 1024).toFixed(2)} KB • {new Date(file.last_modified).toLocaleDateString()}
                        </div>
                      </div>
                      <a
                        href={file.presigned_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          padding: '0.375rem',
                          color: '#2563eb',
                          textDecoration: 'none',
                        }}
                        title="Preview file"
                      >
                        <Eye size={16} />
                      </a>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setShowS3Browser(false);
                  setSelectedS3Files([]);
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'var(--theme-surface, #ffffff)',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddS3Files}
                disabled={selectedS3Files.length === 0}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  background: selectedS3Files.length > 0 ? '#2563eb' : '#d1d5db',
                  color: '#fff',
                  cursor: selectedS3Files.length > 0 ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontWeight: '500',
                }}
              >
                <CheckCircle size={16} />
                Add Selected ({selectedS3Files.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewInvoices;